import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Box, Typography, Divider, Autocomplete,
  FormControl, InputLabel, Select, MenuItem, Alert,
} from '@mui/material';
import { useDescuentoJudicialStore } from '../store/descuentoJudicialStore';
import { TrabajadorService } from '../../trabajadores/services/trabajadorService';
import type { Trabajador } from '../../trabajadores/types/trabajador';
import type { DescuentoJudicial } from '../types/descuentoJudicial';
import { TIPOS_DESCUENTO, TIPOS_CALCULO, ESTADOS_DESCUENTO, initialDescuentoJudicial } from '../types/descuentoJudicial';

interface Props {
  open: boolean;
  onClose: (saved?: boolean) => void;
  descuento: DescuentoJudicial | null;
}

export default function EditDescuentoJudicial({ open, onClose, descuento }: Props) {
  const { createDescuento, updateDescuento } = useDescuentoJudicialStore();
  const [form, setForm] = useState<Partial<DescuentoJudicial>>(initialDescuentoJudicial);
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [selectedTrabajador, setSelectedTrabajador] = useState<Trabajador | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadTrabajadores();
      if (descuento) {
        setForm({ ...descuento });
      } else {
        setForm({ ...initialDescuentoJudicial });
      }
    }
  }, [open, descuento]);

  const loadTrabajadores = async () => {
    try {
      const data = await TrabajadorService.getAll();
      setTrabajadores(data.filter(t => t.activo));
      if (descuento) {
        const trab = data.find(t => t.idTrabajador === descuento.idTrabajador);
        setSelectedTrabajador(trab ?? null);
      } else {
        setSelectedTrabajador(null);
      }
    } catch { /* handled */ }
  };

  const handleChange = (field: string, value: string | number | null) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSave = async () => {
    if (!selectedTrabajador) {
      setError('Seleccione un trabajador');
      return;
    }
    if (!form.beneficiario?.trim()) {
      setError('El beneficiario es obligatorio');
      return;
    }
    if (!form.fechaInicio) {
      setError('La fecha de inicio es obligatoria');
      return;
    }
    if (form.tipoCalculo === 'PORCENTAJE') {
      if (!form.porcentaje || form.porcentaje <= 0 || form.porcentaje > 60) {
        setError('El porcentaje debe ser entre 0.01 y 60%');
        return;
      }
    } else {
      if (!form.montoFijo || form.montoFijo <= 0) {
        setError('El monto fijo debe ser mayor a 0');
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      const data: Partial<DescuentoJudicial> = {
        ...form,
        idTrabajador: selectedTrabajador.idTrabajador,
      };

      if (descuento) {
        await updateDescuento(data);
      } else {
        await createDescuento(data);
      }
      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!descuento;

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{isEditing ? 'Editar' : 'Nuevo'} Descuento Judicial</DialogTitle>
      <DialogContent>
        {/* Trabajador */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', mt: 1 }}>
          Trabajador
        </Typography>
        <Autocomplete
          size="small"
          options={trabajadores}
          getOptionLabel={(t) => `${t.personal?.personaContacto ?? ''} (${t.idTrabajador})`}
          value={selectedTrabajador}
          onChange={(_, val) => setSelectedTrabajador(val)}
          renderInput={(params) => <TextField {...params} label="Buscar trabajador" />}
          sx={{ mb: 2 }}
          disabled={isEditing}
        />

        <Divider sx={{ my: 1 }} />

        {/* Tipo y Cálculo */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Tipo y Cálculo
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Tipo Descuento</InputLabel>
            <Select value={form.tipoDescuento ?? 'PENSION_ALIMENTICIA'} label="Tipo Descuento"
              onChange={(e) => handleChange('tipoDescuento', e.target.value)}>
              {TIPOS_DESCUENTO.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Tipo Cálculo</InputLabel>
            <Select value={form.tipoCalculo ?? 'PORCENTAJE'} label="Tipo Cálculo"
              onChange={(e) => handleChange('tipoCalculo', e.target.value)}>
              {TIPOS_CALCULO.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          {form.tipoCalculo === 'PORCENTAJE' ? (
            <TextField label="Porcentaje (%)" type="number" size="small" fullWidth
              value={form.porcentaje ?? 0}
              onChange={(e) => handleChange('porcentaje', Number(e.target.value))}
              inputProps={{ min: 0.01, max: 60, step: 0.5 }}
              helperText="Máximo 60% por ley" />
          ) : (
            <TextField label="Monto Fijo (S/)" type="number" size="small" fullWidth
              value={form.montoFijo ?? 0}
              onChange={(e) => handleChange('montoFijo', Number(e.target.value))}
              inputProps={{ min: 0.01, step: 10 }} />
          )}
          <FormControl size="small" fullWidth>
            <InputLabel>Estado</InputLabel>
            <Select value={form.estado ?? 'ACTIVO'} label="Estado"
              onChange={(e) => handleChange('estado', e.target.value)}>
              {ESTADOS_DESCUENTO.map(e => <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Vigencia */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Vigencia
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <TextField label="Fecha Inicio" type="date" size="small" fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.fechaInicio?.substring(0, 10) ?? ''}
            onChange={(e) => handleChange('fechaInicio', e.target.value)} />
          <TextField label="Fecha Fin (opcional)" type="date" size="small" fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.fechaFin?.substring(0, 10) ?? ''}
            onChange={(e) => handleChange('fechaFin', e.target.value || null)}
            helperText="Dejar vacío para indefinido" />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Datos del expediente */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Datos del Expediente
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <TextField label="N° Expediente" size="small" fullWidth
            value={form.numeroExpediente ?? ''}
            onChange={(e) => handleChange('numeroExpediente', e.target.value || null)} />
          <TextField label="Juzgado" size="small" fullWidth
            value={form.juzgado ?? ''}
            onChange={(e) => handleChange('juzgado', e.target.value || null)} />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Beneficiario */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Beneficiario
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1 }}>
          <TextField label="Beneficiario" size="small" fullWidth required
            value={form.beneficiario ?? ''}
            onChange={(e) => handleChange('beneficiario', e.target.value)} />
          <TextField label="DNI Beneficiario" size="small" fullWidth
            value={form.dniBeneficiario ?? ''}
            onChange={(e) => handleChange('dniBeneficiario', e.target.value || null)}
            inputProps={{ maxLength: 15 }} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <TextField label="Cuenta Bancaria" size="small" fullWidth
            value={form.cuentaBancaria ?? ''}
            onChange={(e) => handleChange('cuentaBancaria', e.target.value || null)} />
          <TextField label="Banco" size="small" fullWidth
            value={form.banco ?? ''}
            onChange={(e) => handleChange('banco', e.target.value || null)} />
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Motivo y Observaciones */}
        <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
          Motivo y Observaciones
        </Typography>
        <TextField label="Motivo" size="small" fullWidth multiline rows={2} sx={{ mb: 1.5 }}
          value={form.motivo ?? ''}
          onChange={(e) => handleChange('motivo', e.target.value || null)} />
        <TextField label="Observaciones" size="small" fullWidth multiline rows={2}
          value={form.observaciones ?? ''}
          onChange={(e) => handleChange('observaciones', e.target.value || null)} />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}

        {form.tipoCalculo === 'PORCENTAJE' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            El descuento se calculará sobre el sueldo neto (remuneraciones - aportes obligatorios - renta 5ta).
            Máximo permitido por ley: 60%.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
